import React, { useState } from 'react';
import { useMissions } from '@/hooks/useMissions';
import MissionTabs from '@/components/missions/MissionTabs';
import MissionHeader from '@/components/missions/MissionHeader';
import MainGoalCard from '@/components/missions/MainGoalCard';
import DailyQuestsList from '@/components/missions/DailyQuestsList';
import RewardForm from '@/components/missions/RewardForm';
import { MissionCompletionCard } from '@/components/missions/MissionCompletionCard';
import { RewardSubmittedCard } from '@/components/missions/RewardSubmittedCard';
import { Loader2 } from 'lucide-react';
import MissionsHeader from '@/components/missions/MissionsHeader';

const Missions = () => {
  const { missionData, loading, validating, validateAllMissions, submitRewardInfo, refreshMissionData, rewardShipment } = useMissions();
  const [activeTab, setActiveTab] = useState<'goals' | 'badges'>('goals');

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center px-4">
        <div className="text-center bg-white/5 border-white/10 rounded-2xl p-6 shadow-xl">
          <Loader2 className="w-12 h-12 animate-spin text-luxury-gold mx-auto mb-4" />
          <p className="text-white font-medium text-sm">Carregando suas missões...</p>
        </div>
      </div>
    );
  }

  if (!missionData?.mission || !missionData?.userMission) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-black via-black to-gray-900 p-4">
        <div className="text-center bg-white/5 border-white/10 rounded-2xl p-6 shadow-xl max-w-md">
          <div className="w-16 h-16 bg-luxury-gold/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">🏆</span>
          </div>
          <h2 className="text-lg font-semibold text-white mb-2">
            Nenhuma missão ativa
          </h2>
          <p className="text-white/60 text-sm">
            Aguarde novas missões serem disponibilizadas!
          </p>
        </div>
      </div>
    );
  }

  const { mission, steps, userMission, progress } = missionData;
  const isCompleted = userMission.status === 'completed';
  const completedDays = progress.filter(p => p.status === 'completed').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 p-4 sm:p-6 lg:p-8 pb-24 md:pb-6">
      {/* Mobile Header */}
      <MissionsHeader />
      
      <div className="max-w-4xl mx-auto">
        {/* Tabs */}
        <MissionTabs activeTab={activeTab} onTabChange={setActiveTab} />

        {activeTab === 'goals' && (
          <>
            {/* Mission Header */}
            <MissionHeader
              title={mission.title}
            />

            {/* Main Goal Card */}
            <MainGoalCard
              totalDays={steps.length}
              completedDays={completedDays}
            />

            {/* Reward Submitted Card or Mission Content */}
            {rewardShipment ? (
              <RewardSubmittedCard 
                shipment={rewardShipment}
                missionTitle={mission.title}
              />
            ) : isCompleted ? (
              <div className="space-y-6">
                <MissionCompletionCard
                  totalDays={steps.length}
                  completedDays={completedDays}
                  startedAt={userMission.started_at}
                  completedAt={userMission.completed_at}
                />
                
                <RewardForm onSubmit={submitRewardInfo} />
              </div>
            ) : (
              <DailyQuestsList
                steps={steps}
                progress={progress}
                currentDay={userMission.current_day}
                onValidateTask={async () => {
                  const result = await validateAllMissions();
                  if (result) {
                    await refreshMissionData();
                  }
                }}
                validating={validating}
              />
            )}
          </>
        )}

        {activeTab === 'badges' && (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-3xl">🏆</span>
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">
              Em breve!
            </h2>
            <p className="text-white/60 text-sm">
              Sistema de badges e conquistas chegando em breve.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Missions;
